import { useCallback, useEffect, useRef, useState } from "react";
import {
  getOfflineQueueSummary, offlineQueueErrorCode, replayOfflineMutations
} from "./offline-queue";
import type { OfflineReplayResult } from "./offline-queue";
import { SESSION_CONTEXT_CHANGED_EVENT, SESSION_UPDATED_EVENT } from "../api/client";
import { OFFLINE_QUEUE_EVENT } from "../brand";

export function useOfflineQueue() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [requiresReview, setRequiresReview] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const epoch = useRef(0);
  const readRevision = useRef(0);
  const activeSync = useRef<Promise<OfflineReplayResult> | null>(null);

  const refresh = useCallback(async () => {
    const revision = ++readRevision.current;
    try {
      const summary = await getOfflineQueueSummary();
      if (revision !== readRevision.current) return;
      setPendingCount(summary.pendingCount);
      setRequiresReview(summary.requiresReview);
    } catch (error) {
      if (revision !== readRevision.current) return;
      setPendingCount(0);
      setRequiresReview(false);
      setErrorCode(offlineQueueErrorCode(error));
    }
  }, []);

  const sync = useCallback((): Promise<OfflineReplayResult> => {
    if (activeSync.current) return activeSync.current;
    const started = epoch.current;
    const task = (async () => {
      setSyncing(true);
      setErrorCode(null);
      try {
        const result = await replayOfflineMutations();
        if (started === epoch.current) {
          setErrorCode(result.errorCode);
          await refresh();
        }
        return result;
      } catch (error) {
        const code = offlineQueueErrorCode(error);
        if (started === epoch.current) { setErrorCode(code); await refresh(); }
        return { completed: 0, failed: 0, errorCode: code };
      } finally {
        if (started === epoch.current) setSyncing(false);
        activeSync.current = null;
      }
    })();
    activeSync.current = task;
    return task;
  }, [refresh]);

  useEffect(() => {
    const onlineHandler = () => {
      setOnline(true);
      void sync();
    };
    const offlineHandler = () => setOnline(false);
    // Cross-tab notifications are optional; Web Locks remain the replay gate.
    let channel: BroadcastChannel | null = null;
    try { if (typeof BroadcastChannel !== "undefined") channel = new BroadcastChannel(OFFLINE_QUEUE_EVENT); } catch { /* focus refresh remains available */ }
    if (channel) channel.onmessage = () => void refresh();
    const queueHandler = () => { void refresh(); channel?.postMessage("changed"); };
    const focusHandler = () => void refresh();
    const sessionHandler = () => {
      epoch.current++;
      readRevision.current++;
      setPendingCount(0);
      setRequiresReview(false);
      setSyncing(false);
      setErrorCode(null);
      void refresh();
    };

    window.addEventListener("online", onlineHandler);
    window.addEventListener("offline", offlineHandler);
    window.addEventListener("focus", focusHandler);
    window.addEventListener(SESSION_UPDATED_EVENT, sessionHandler);
    window.addEventListener(SESSION_CONTEXT_CHANGED_EVENT, sessionHandler);
    window.addEventListener(
      OFFLINE_QUEUE_EVENT,
      queueHandler
    );

    void refresh();

    return () => {
      epoch.current++;
      readRevision.current++;
      channel?.close();
      window.removeEventListener("online", onlineHandler);
      window.removeEventListener("offline", offlineHandler);
      window.removeEventListener("focus", focusHandler);
      window.removeEventListener(SESSION_UPDATED_EVENT, sessionHandler);
      window.removeEventListener(SESSION_CONTEXT_CHANGED_EVENT, sessionHandler);
      window.removeEventListener(
        OFFLINE_QUEUE_EVENT,
        queueHandler
      );
    };
  }, [refresh, sync]);

  return {
    online,
    pendingCount,
    requiresReview,
    errorCode,
    syncing,
    refresh,
    sync
  };
}
